<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue';

import arrowRightIcon from '@clovyr/bed/assets/images/icons/ai-landing/arrow-right.svg';
import BaseSvg from '@clovyr/bed/components/icons/BaseSvg.vue';
import type { Manifest } from '@clovyr/pollen/manifest/manifest';

import { useLaunchButton } from '@/composables/useLaunchButton';

const props = defineProps<{
  app: Manifest;
  onLaunch?: () => void;
}>();

const { handleLaunchClicked } = useLaunchButton(props.app);

const launchFromCTA = () => {
  if (props.onLaunch) {
    props.onLaunch();
    return;
  }

  handleLaunchClicked();
};

const ctaVisible = ref(false);
const ctaRef = ref<HTMLElement | null>(null);

let observer: IntersectionObserver | undefined;

onMounted(() => {
  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return;
        }

        ctaVisible.value = true;
        observer?.disconnect();
      });
    },
    {
      threshold: 0.15,
    },
  );

  if (ctaRef.value) {
    observer.observe(ctaRef.value);
  }
});

onBeforeUnmount(() => {
  observer?.disconnect();
});
</script>

<template>
  <section class="ai-cta">
    <div class="ai-cta__shell">
      <div class="ai-cta__halo"></div>

      <div ref="ctaRef" class="ai-cta__inner" :class="{ 'is-visible': ctaVisible }">
        <div class="ai-cta__grid"></div>
        <div class="ai-cta__corner ai-cta__corner--top"></div>
        <div class="ai-cta__corner ai-cta__corner--bottom"></div>

        <div class="ai-cta__content">
          <h2>
            Own your AI.
            <span> Own your future.</span>
          </h2>

          <p>
            Join thousands of teams who've taken back control of their AI stack. Private, powerful,
            and entirely yours.
          </p>

          <div class="ai-cta__actions">
            <button type="button" class="ai-cta__btn" @click="launchFromCTA">
              <span>Launch Clovyr AI Cloud</span>
              <BaseSvg class="ai-cta__btn-icon" viewBox="0 0 24 24" aria-hidden="true" role="img">
                <image
                  :href="arrowRightIcon"
                  x="0"
                  y="0"
                  width="100%"
                  height="100%"
                  preserveAspectRatio="xMidYMid meet"
                />
              </BaseSvg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.ai-cta {
  background: #221a33;
  overflow: hidden;
  padding: 7rem 2rem;
}

.ai-cta__shell {
  position: relative;
  max-width: 900px;
  margin: 0 auto;
}

.ai-cta__halo {
  position: absolute;
  inset: 0;
  border-radius: 1.5rem;
  background: linear-gradient(
    to bottom right,
    rgba(0, 191, 116, 0.1),
    transparent,
    rgba(124, 58, 237, 0.1)
  );
  filter: blur(32px);
}

.ai-cta__inner {
  position: relative;
  border: 1px solid rgba(0, 191, 116, 0.2);
  border-radius: 1.5rem;
  background: #2a1f40;
  padding: 4rem 3rem;
  text-align: center;
  overflow: hidden;
  opacity: 0;
  transform: translateY(30px);
}

.ai-cta__inner.is-visible {
  animation: fadeLift 0.55s ease forwards;
}

.ai-cta__grid {
  position: absolute;
  inset: 0;
  opacity: 0.035;
  background-image:
    linear-gradient(#00bf74 1px, transparent 1px),
    linear-gradient(90deg, #00bf74 1px, transparent 1px);
  background-size: 40px 40px;
}

.ai-cta__corner {
  position: absolute;
  width: 8rem;
  height: 8rem;
}

.ai-cta__corner--top {
  top: 0;
  left: 0;
  background: rgba(0, 191, 116, 0.05);
  border-bottom-right-radius: 999px;
}

.ai-cta__corner--bottom {
  right: 0;
  bottom: 0;
  background: rgba(124, 58, 237, 0.05);
  border-top-left-radius: 999px;
}

.ai-cta__content {
  position: relative;
}

.ai-cta__content h2 {
  margin: 0 0 1.25rem;
  color: #fff;
  font-size: clamp(3rem, 6vw, 3.75rem);
  line-height: 1;
  font-weight: 900;
  letter-spacing: -0.025em;
}

.ai-cta__content h2 span {
  background: linear-gradient(to right, #00bf74, #00e874);
  color: transparent;
  -webkit-background-clip: text;
  background-clip: text;
}

.ai-cta__content p {
  margin: 0 auto 2.5rem;
  max-width: 560px;
  color: #9ca3af;
  font-size: 1.125rem;
  line-height: 1.625;
}

.ai-cta__actions {
  display: flex;
  justify-content: center;
}

.ai-cta__btn {
  appearance: none;
  border: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  border-radius: 0.75rem;
  background: #00bf74;
  color: #000;
  text-decoration: none;
  font-size: 1rem;
  font-weight: 700;
  transition: background-color 200ms ease;
}

.ai-cta__btn:hover {
  background: #00e874;
}

.ai-cta__btn-icon {
  width: 1.125rem;
  height: 1.125rem;
}

@media (max-width: 1023px) {
  .ai-cta {
    padding: 6.25rem 1.75rem;
  }

  .ai-cta__inner {
    padding: 3.25rem 2rem;
  }

  .ai-cta__content h2 {
    font-size: clamp(2.75rem, 8vw, 3.4rem);
  }

  .ai-cta__content p {
    font-size: 1.02rem;
    line-height: 1.58;
  }
}

@media (max-width: 800px) {
  .ai-cta {
    padding: 5.5rem 1.5rem;
  }

  .ai-cta__inner {
    padding: 3rem 1.5rem;
  }

  .ai-cta__content h2 {
    font-size: clamp(2.5rem, 8vw, 3rem);
    line-height: 1.05;
  }
}

@media (max-width: 520px) {
  .ai-cta {
    padding: 5rem 1rem;
  }

  .ai-cta__inner {
    padding: 2.2rem 1rem;
  }

  .ai-cta__content p {
    margin-bottom: 1.75rem;
    font-size: 0.95rem;
  }

  .ai-cta__btn {
    width: 100%;
    padding: 0.9rem 1rem;
  }
}

@keyframes fadeLift {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
